import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trophy, XCircle, CalendarX, Clock } from "lucide-react";
import { MobileDialog } from "../mobile";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * TestDayResultModal — end-of-lesson prompt.
 *
 * Fair-wording rules:
 *   • Only linked instructors can log.
 *   • "learner passes supported" (never "pass rate").
 *   • Passed → learner + instructor passports both update.
 *   • Did not pass → learner-only private history.
 *   • Cancelled / moved → log only, no counts.
 *   • Not recorded yet → leaves a prompt in the instructor's pending list.
 */
export default function TestDayResultModal({
  learnerId,
  learnerName,
  open,
  onClose,
  onLogged,
}) {
  const [outcome, setOutcome] = useState(null);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [testCentre, setTestCentre] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const today = new Date().toISOString().slice(0, 10);

  const choices = [
    { id: "passed",          label: "Passed",                  icon: Trophy,     cls: "bg-emerald-600 hover:bg-emerald-500 text-white" },
    { id: "not_passed",      label: "Did not pass",            icon: XCircle,    cls: "bg-amber-600 hover:bg-amber-500 text-white" },
    { id: "cancelled_moved", label: "Cancelled / moved",       icon: CalendarX,  cls: "bg-slate-700 hover:bg-slate-600 text-white" },
    { id: "not_recorded_yet",label: "Not recorded yet",        icon: Clock,      cls: "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700" },
  ];

  const submit = async () => {
    if (!outcome) { toast.error("Pick an outcome first."); return; }
    setSaving(true);
    try {
      const body = {
        learner_id: learnerId,
        test_date: today,
        outcome,
        notes: notes || null,
        reason: reason || null,
        test_centre: testCentre || null,
      };
      const { data } = await axios.post(`${API}/api/test-results/log`, body, { withCredentials: true });
      const messages = {
        passed: "Passed recorded. Learner & instructor passports updated.",
        not_passed: "Result saved privately on the learner's passport.",
        cancelled_moved: "Logged as cancelled / moved.",
        not_recorded_yet: "Saved. We'll remind you to record the outcome.",
      };
      toast.success(messages[outcome] || "Saved.");
      onLogged?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save result.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileDialog
      bareContent
      open={open}
      onClose={onClose}
      testid="test-day-result-modal"
    >
      <div className="w-full sm:max-w-md bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl border border-slate-200 dark:border-slate-700 shadow-2xl p-5 sm:p-6">
        <div className="mb-4">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-600">Test Day Result</p>
          <h2 className="mt-1 text-xl font-extrabold text-slate-900 dark:text-white">What happened today?</h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {learnerName ? `Logging for ${learnerName}. ` : ""}Learner passes supported — fair, factual records only.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          {choices.map(({ id, label, icon: Icon, cls }) => (
            <button
              key={id}
              data-testid={`tdr-${id}`}
              onClick={() => setOutcome(id)}
              className={`px-3 py-3 rounded-2xl text-xs font-extrabold flex flex-col items-center gap-1 transition ${
                outcome === id ? cls + " ring-2 ring-offset-2 ring-indigo-500" : cls
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {outcome === "passed" && (
          <div className="space-y-2 mb-3">
            <TextInput label="Test centre (optional)" value={testCentre} onChange={setTestCentre} testid="tdr-centre" />
            <TextArea  label="Next focus areas / examiner notes (optional, private to learner)" value={notes} onChange={setNotes} testid="tdr-notes" />
            <p className="text-[11px] text-emerald-700 dark:text-emerald-300">Will add +1 verified learner pass supported to your Professional Passport.</p>
          </div>
        )}
        {outcome === "not_passed" && (
          <div className="space-y-2 mb-3">
            <TextInput label="Test centre (optional)" value={testCentre} onChange={setTestCentre} testid="tdr-centre" />
            <TextArea  label="Focus areas to recover (private — only the learner & you see this)" value={notes} onChange={setNotes} testid="tdr-notes" />
            <p className="text-[11px] text-amber-700 dark:text-amber-300">Stays private on the learner's passport. We'll suggest a Mastery Builder recovery plan.</p>
          </div>
        )}
        {outcome === "cancelled_moved" && (
          <div className="space-y-2 mb-3">
            <TextArea label="Reason (optional)" value={reason} onChange={setReason} testid="tdr-reason" />
            <p className="text-[11px] text-slate-500 dark:text-slate-400">No pass or fail counted.</p>
          </div>
        )}
        {outcome === "not_recorded_yet" && (
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">We'll keep this on your pending list so you can finish it later.</p>
        )}

        <div className="flex gap-2">
          <button data-testid="tdr-cancel" onClick={onClose} className="flex-1 px-3 py-2.5 text-xs font-bold rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">Later</button>
          <button
            data-testid="tdr-submit"
            onClick={submit}
            disabled={saving || !outcome}
            className="flex-1 px-3 py-2.5 text-xs font-extrabold rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60 flex items-center justify-center gap-1"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save result
          </button>
        </div>
      </div>
    </MobileDialog>
  );
}

function TextInput({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">{label}</span>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white" />
    </label>
  );
}
function TextArea({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">{label}</span>
      <textarea data-testid={testid} rows={3} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white resize-none" />
    </label>
  );
}
